// The game view: connect, keep the server clock, feed snapshots to the replica, capture input,
// and render at the display's rate from an interpolated view a little behind server time.
// Mounted by the shell into its overlay; `stop()` tears everything down so the feed can come back.

import { loadModule, type ClientWorld, type Entity, type GameModule, type Meta } from "../contract.js";
import type { ServerMsg } from "../shared/protocol.js";
import { ServerClock } from "../shared/clock.js";
import { games } from "../../games/index.js";
import { serverAddress, NO_SERVER_MESSAGE } from "./config.js";
import { Replica } from "./replica.js";
import { Camera } from "./camera.js";
import { InputController } from "./input.js";
import { drawFrame } from "./render.js";
import { spriteFor } from "./sprites.js";
import type { LocalServer, Transport } from "./localserver.js";

export interface GameMount {
  /** Catalogue slug, e.g. "blobs". */
  game: string;
  room: string;
  name: string;
  canvas: HTMLCanvasElement;
  hud: HTMLElement;
  board: HTMLElement;
  /** On-screen A/B buttons for touch devices (data-btn="a" | "b"). */
  buttons?: HTMLElement[];
  /**
   * A draft from the Create tab: its module, and the in-page server to connect to instead of
   * the network. `game` is then only a label.
   */
  local?: { module: GameModule; server: LocalServer };
}

export interface GameHandle {
  /** The loaded module's meta (name, controls line), for the shell's controls card. */
  meta: Meta;
  stop(): void;
}

const INPUT_INTERVAL_MS = 50;
const INPUT_KEEPALIVE_MS = 500;
const PING_INTERVAL_MS = 1000;

export async function mountGame(o: GameMount): Promise<GameHandle> {
  let game: GameModule;
  if (o.local) game = o.local.module;
  else {
    const entry = games[o.game];
    if (!entry) throw new Error(`unknown game "${o.game}"`);
    game = await loadModule(entry.load);
  }
  const address = o.local ? { ws: "", http: "" } : serverAddress();
  const tickMs = 1000 / game.meta.tickRate;
  /** Render this far behind the estimated server clock so there is usually a newer snapshot to interpolate toward. */
  const interpDelayMs = tickMs * 2 + 20;

  const { canvas, hud, board } = o;
  const ctx = canvas.getContext("2d")!;
  const ac = new AbortController();
  const { signal } = ac;

  let replica = new Replica();
  const clock = new ServerClock();
  const camera = new Camera();
  const input = new InputController(canvas, signal, o.buttons ?? []);

  let ws: Transport | null = null;
  let wsOpen = false;
  let token: string | undefined;
  // Where the room runs, for the HUD. Every browser is a client and nobody hosts: the first
  // player wakes a room on the server, the thirty-second joins the same one. In dev the page's
  // own origin proxies to the local server, so name the origin rather than claim more.
  const fullHost = address ? address.ws.replace(/^wss?:\/\//, "") : "";
  // A workers.dev address is long enough to wrap the HUD into the leaderboard on a phone.
  const host = o.local ? "this page" : /\.workers\.dev$/.test(fullHost) ? "Cloudflare" : fullHost;
  let status = address ? `connecting to ${host}` : NO_SERVER_MESSAGE;
  let stopped = false;
  let reconnectTimer: number | undefined;
  let bytesIn = 0;
  let bytesInAtSample = 0;
  let kbps = 0;
  let lastKbpsAt = performance.now();
  let lastInputSent = "";
  let lastInputAt = 0;
  let frames = 0;
  let fps = 0;
  let lastFpsAt = performance.now();
  let lastRenderSelf: Entity | undefined;
  let currentEntities: Entity[] = [];

  const world: ClientWorld = {
    meta: game.meta,
    w: game.meta.world.w,
    h: game.meta.world.h,
    get you() {
      return replica.you;
    },
    time: 0,
    entities: () => currentEntities,
    get: (id) => currentEntities.find((e) => e.id === id),
    name: (pid) => replica.name(pid),
    sprite: spriteFor,
  };

  // ---- networking ----------------------------------------------------------------------------

  function connect(): void {
    if (stopped || !address) return;
    status = token ? `reconnecting to ${host}` : `connecting to ${host}`;
    const sock = o.local
      ? o.local.server.connect(o.room)
      : openSocket(`${address.ws}/ws?game=${encodeURIComponent(o.game)}&room=${encodeURIComponent(o.room)}`);
    ws = sock;
    sock.onopen = () => {
      wsOpen = true;
      sock.send(JSON.stringify({ t: "hello", name: o.name, resume: token }));
    };
    sock.onmessage = (data) => {
      bytesIn += data.length;
      let msg: ServerMsg;
      try {
        msg = JSON.parse(data) as ServerMsg;
      } catch {
        return;
      }
      switch (msg.t) {
        case "welcome":
          token = msg.token;
          replica = new Replica(); // the server starts this socket from a blank view
          clock.seed(msg.time, performance.now());
          status = `room ${msg.room} runs on ${host}`;
          break;
        case "s":
          replica.apply(msg);
          break;
        case "pong":
          clock.sample(msg.c, msg.s, performance.now());
          break;
        case "err":
          status = `${host} says: ${msg.msg}`;
          break;
      }
    };
    sock.onclose = (code) => {
      if (ws !== sock) return;
      wsOpen = false;
      if (stopped) return;
      ws = null;
      status = code === 1011 ? status : `lost ${host}, retrying`;
      reconnectTimer = window.setTimeout(connect, 1000);
    };
  }

  function send(obj: unknown): void {
    if (ws && wsOpen) ws.send(JSON.stringify(obj));
  }

  const pingTimer = window.setInterval(() => send({ t: "ping", c: performance.now() }), PING_INTERVAL_MS);

  function pumpInput(now: number): void {
    const self = lastRenderSelf;
    const raw = input.current(self, (sx, sy) => camera.toWorld(sx, sy, canvas.clientWidth, canvas.clientHeight));
    const key = JSON.stringify(raw);
    const due = now - lastInputAt >= INPUT_INTERVAL_MS;
    if ((due && key !== lastInputSent) || now - lastInputAt >= INPUT_KEEPALIVE_MS || input.urgent) {
      send({ t: "in", i: raw });
      lastInputSent = key;
      lastInputAt = now;
    }
  }
  window.addEventListener("blur", () => pumpInput(performance.now()), { signal });

  // ---- rendering -----------------------------------------------------------------------------

  function resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
  }

  let raf = 0;
  let lastFrame = performance.now();
  function frame(now: number): void {
    if (stopped) return;
    const dt = Math.min(0.1, (now - lastFrame) / 1000);
    lastFrame = now;
    resize();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    const renderTime = clock.now(now) - interpDelayMs;
    const { entities, alpha } = replica.sample(renderTime);
    currentEntities = entities;
    (world as { time: number }).time = renderTime / 1000;
    const self = replica.you === undefined ? undefined : entities.find((e) => e.id === replica.you);
    lastRenderSelf = self;

    camera.follow(self, w, h, dt, world.w, world.h);
    drawFrame(ctx, w, h, dpr, camera, entities, game, world, alpha);

    pumpInput(now);

    frames++;
    if (now - lastFpsAt >= 1000) {
      fps = Math.round((frames * 1000) / (now - lastFpsAt));
      frames = 0;
      lastFpsAt = now;
    }
    if (now - lastKbpsAt >= 1000) {
      kbps = (bytesIn - bytesInAtSample) / ((now - lastKbpsAt) / 1000) / 1024;
      bytesInAtSample = bytesIn;
      lastKbpsAt = now;
      updateHud();
    }
    raf = requestAnimationFrame(frame);
  }

  function updateHud(): void {
    const ping = Number.isFinite(clock.rtt) ? `${Math.round(clock.rtt)} ms` : "–";
    const others = Math.max(0, replica.players - 1);
    const who = others === 0 ? "just you here" : `you and ${others} other${others === 1 ? "" : "s"}`;
    hud.textContent =
      `${game.meta.name} · ${status} · ${who} · ` +
      `ping ${ping} · ${fps} fps · ${kbps.toFixed(1)} KB/s · ${replica.size} entities in view`;
    const rows = replica.leaderboard.map(([pid, score], i) => {
      const me = lastRenderSelf && lastRenderSelf.owner === pid ? " me" : "";
      return `<div class="row${me}"><span>${i + 1}. ${escapeHtml(replica.name(pid))}</span><span>${score}</span></div>`;
    });
    board.innerHTML = rows.length ? `<div class="title">Leaderboard</div>${rows.join("")}` : "";
  }

  connect();
  updateHud();
  raf = requestAnimationFrame(frame);

  return {
    meta: game.meta,
    stop() {
      if (stopped) return;
      stopped = true;
      ac.abort();
      cancelAnimationFrame(raf);
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      const sock = ws;
      ws = null;
      sock?.close();
      hud.textContent = "";
      board.innerHTML = "";
    },
  };
}

/** A WebSocket in the Transport shape the in-page server also speaks. */
function openSocket(url: string): Transport {
  const sock = new WebSocket(url);
  sock.binaryType = "arraybuffer";
  const t: Transport = {
    onopen: null,
    onmessage: null,
    onclose: null,
    send: (text) => {
      if (sock.readyState === WebSocket.OPEN) sock.send(text);
    },
    close: () => sock.close(),
  };
  sock.onopen = () => t.onopen?.();
  sock.onmessage = (ev) => t.onmessage?.(typeof ev.data === "string" ? ev.data : new TextDecoder().decode(ev.data as ArrayBuffer));
  sock.onclose = (ev) => t.onclose?.(ev.code, ev.reason);
  sock.onerror = () => sock.close();
  return t;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
